import { useEffect, useMemo, useState } from 'react'
import type { CameraStatus, Site } from './types'
import { fetchCameraHealth } from './api'
import { HEALTH_POLL_MS } from './constants'

/** Camera status for every site, keyed site → camera. */
export type SiteHealth = Record<string, Record<string, CameraStatus>>

/** What the hook reports: per-camera status, plus the sites that did not answer. */
export interface CameraHealth {
  health: SiteHealth
  unreachable: ReadonlySet<string>
}

/**
 * Polls camera health for every configured site.
 *
 * Called once, at the top. Health is polled rather than pushed because a camera
 * going down produces no event — that is the whole problem — so there is
 * nothing for SSE to carry. Three separate components need the answer, and each
 * polling for itself meant three requests per site per interval against an
 * endpoint that reaches across WireGuard.
 *
 * A failed poll keeps the last answer: a health endpoint that is briefly
 * unreachable should not blank every camera on screen.
 */
export function useCameraHealth(sites: Record<string, Site>): CameraHealth {
  const [health, setHealth] = useState<SiteHealth>({})
  // Tracked alongside health rather than folded into it: a failed poll says
  // nothing about any individual camera, only that the site as a whole did not
  // answer. Health deliberately keeps its last answer through a blip.
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set())
  const siteIds = Object.keys(sites).sort().join(',')

  useEffect(() => {
    if (!siteIds) return
    let cancelled = false

    const ids = siteIds.split(',')

    const poll = () => {
      ids.forEach((siteId) => {
        fetchCameraHealth(siteId)
          .then((h) => {
            if (cancelled) return
            setHealth((prev) => ({ ...prev, [siteId]: h }))
            setFailed((prev) => without(prev, siteId))
          })
          .catch(() => {
            if (!cancelled) setFailed((prev) => with_(prev, siteId))
          })
      })
    }

    poll()
    const t = setInterval(poll, HEALTH_POLL_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [siteIds])

  // Reported by derivation rather than stored pruned: a site can disappear
  // because config changed or because the user's access to it was withdrawn,
  // and in both cases whatever it last said is stale the moment it goes.
  return useMemo(() => {
    const ids = siteIds ? siteIds.split(',') : []
    const visible: SiteHealth = {}
    ids.forEach((id) => { if (health[id]) visible[id] = health[id] })
    // Reported by derivation for the same reason health is: a site can vanish
    // from config or have access withdrawn, and whatever it last said — failing
    // included — is stale the moment it goes.
    const unreachable = new Set(ids.filter((id) => failed.has(id)))
    return { health: visible, unreachable }
  }, [siteIds, health, failed])
}

/** Set helpers that return a new set only when the membership actually changes,
 *  so a steady stream of successful polls does not re-render every consumer. */
function with_(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (set.has(id)) return set
  const next = new Set(set)
  next.add(id)
  return next
}

function without(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!set.has(id)) return set
  const next = new Set(set)
  next.delete(id)
  return next
}
